
/**
 * FormSubmission Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';

export class FormSubmissionService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    /**
     * Get all items with pagination
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                meta: response.meta || {},
                message: response.message || 'FormSubmission listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch form-submission list');
        }
    }

    /**
     * Get item by ID
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch form-submission');
        }
    }

    async getSurveyResponses(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getSurveyResponses(params),
                { maxRetries: 2 },
            );
            return {
                success: true,
                data: response.data || [],
                page: response.page,
                pageSize: response.pageSize,
                total: response.total,
                totalPages: response.totalPages,
                uniqueValues: response.uniqueValues || {},
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch survey responses');
        }
    }

    async getSurveyResponseById(id) {
        try {
            const response = await withRetry(
                () => this.api.getSurveyResponseById(id),
                { maxRetries: 2 },
            );
            return {
                success: true,
                data: response.data,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch survey response');
        }
    }

    /**
     * Create new item
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async create(data) {
        const payload = data;

        try {
            const response = await this.api.create(payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'FormSubmission oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create form-submission');
        }
    }

    /**
     * Update item
     * @param {number|string} id
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async update(id, data) {
        const payload = data;

        try {
            const response = await this.api.update(id, payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'FormSubmission güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update form-submission');
        }
    }

    /**
     * Delete item
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async delete(id) {
        try {
            const response = await this.api.delete(id);

            return {
                success: true,
                data: response.data,
                message: response.message || 'FormSubmission silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete form-submission');
        }
    }

    /**
     * Bulk delete items
     * @param {Array<number|string>} ids
     * @returns {Promise<Object>}
     */
    async bulkDelete(ids) {
        const results = { success: [], failed: [] };

        for (const id of ids) {
            try {
                await this.api.delete(id);
                results.success.push(id);
            } catch (error) {
                results.failed.push({ id, error: error.message });
            }
        }

        return {
            success: results.failed.length === 0,
            data: results,
            message: `${results.success.length}/${ids.length} öğe silindi`
        };
    }

    /**
     * Custom operation: createSubmit
     */
    async createSubmit(data = {}) {
        try {
            const response = await this.api.submit(data);
            return {
                success: true,
                data: response,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to execute createSubmit');
        }
    }

    /**
     * Custom operation: createBatchDelete
     */
    async createBatchDelete(data = {}) {
        try {
            const response = await this.api.client.post(`/batch-delete`, data);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to execute createBatchDelete');
        }
    }

    /**
     * Handle and transform errors
     */
    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
